import { Component } from 'react';
import { Navigate, useLocation } from 'react-router';
import Loading from '../common/lib/Loading';
import PageNotFound from '../common/lib/PageNotFound';
import type { MultiLang } from '../config';
import BulletinArchive from './lib/BulletinArchive';
import type BulletinContext from './lib/BulletinContext';
import BulletinStories from './lib/BulletinStories';
import BulletinStory from './lib/BulletinStory';
import BulletinUtils from './lib/BulletinUtils';

interface Props {
    lang: MultiLang;
    mydirname: string;
}

interface State {
    loading: boolean;
    context: BulletinContext | null;
}

const str2int = (str: string | null): number | null => {
    if (str === null || str.match(/^\d+$/) === null) {
        return null;
    }
    return parseInt(str, 10);
};

interface BulletinPageProps {
    lang: MultiLang;
    context: BulletinContext;
}

const BulletinPage = (props: BulletinPageProps) => {
    const { lang, context } = props;
    const { pathname, search, hash } = useLocation();
    if (pathname.replace(/\/$/, '') === context.getUrl('').replace(/\/$/, '')) {
        return <Navigate to={context.getUrl(`index.php${search}${hash}`)} replace />;
    }
    if (pathname !== context.getUrl('index.php')) {
        return <PageNotFound lang={lang} />;
    }
    const params = new URLSearchParams(search);
    const page = params.get('page') || 'main';
    switch (page) {
        case 'main': {
            const topicId = str2int(params.get('storytopic')) || 0;
            const storyNum = str2int(params.get('storynum')) || 5;
            const start = str2int(params.get('start')) || 0;
            return (
                <BulletinStories lang={lang} context={context} topicId={topicId} storyNum={storyNum} start={start} />
            );
        }
        case 'article': {
            const storyId = str2int(params.get('storyid')) || 0;
            return <BulletinStory lang={lang} context={context} id={storyId} />;
        }
        case 'archive': {
            const year = str2int(params.get('year')) || 0;
            const month = str2int(params.get('month')) || 0;
            return <BulletinArchive lang={lang} context={context} year={year} month={month} />;
        }
    }
    return <PageNotFound lang={lang} />;
};

class Bulletin extends Component<Props, State> {
    private isActive: boolean;

    constructor(props: Props) {
        super(props);
        this.state = {
            loading: true,
            context: null,
        };
        this.isActive = false;
    }

    componentDidMount() {
        this.isActive = true;
        this.updateContext();
    }

    componentDidUpdate(prevProps: Props) {
        const { mydirname } = this.props;
        const prevMydirname = prevProps.mydirname;
        if (mydirname !== prevMydirname) {
            this.setState({ loading: true, context: null });
            this.updateContext();
        }
    }

    componentWillUnmount() {
        this.isActive = false;
    }

    async updateContext() {
        const { mydirname } = this.props;
        const context = await BulletinUtils.getContext(mydirname);
        if (this.isActive) {
            this.setState({ loading: false, context });
        }
    }

    render() {
        const { lang, mydirname } = this.props;
        const { loading, context } = this.state;
        if (loading) {
            return <Loading />;
        }
        if (context === null) {
            return <PageNotFound lang={lang} />;
        }
        return (
            <div className={`bulletin ${mydirname}`}>
                <BulletinPage lang={lang} context={context} />
            </div>
        );
    }
}

export default Bulletin;
